import { AuditLogs } from "@/routes/audit-logs";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import "./global.css";
import { AuthProvider, ProtectedRoute } from "@/auth/context";
import { GooglePlaySessionProvider } from "@/client/googlePlay";
import { Analytics } from "@/routes/analytics.tsx";
import { Instances } from "@/routes/instances.tsx";
import Callback from "@/routes/authn/callback.tsx";
import Login from "@/routes/authn/login.tsx";
import { Settings } from "@/routes/settings.tsx";
import StreamPage from "@/routes/stream.tsx";
import SignedStreamPage from "@/routes/signedStream.tsx";
import PreviewPage from "@/routes/preview.tsx";
import { BuildsPage } from "@/routes/builds.tsx";
import { BazelBuildsPage } from "@/routes/bazel-builds.tsx";
import { SnapshotsPage } from "@/routes/snapshots.tsx";
import { SessionsPage } from "@/routes/sessions.tsx";
import { Billing } from "@/routes/billing.tsx";
import { Registry } from "@/routes/registry.tsx";
import { Secrets } from "@/routes/secrets.tsx";
import { Network } from "@/routes/network.tsx";
import { Onboarding } from "@/routes/onboarding.tsx";
import { Toaster } from "@/components/ui/toaster";
import { PylonWidget } from "@/components/ui/pylon";
import { initializeAnalytics } from "@/analytics";

import CLILogin from "@/routes/authn/cli.tsx";
import OAuthAuthorize from "@/routes/authn/oauth.tsx";
import { InvitationDialog } from "@/components/InvitationDialog";

initializeAnalytics();

const ProtectedLayout = () => {
  // Google Play sign-in state lives above the routes so a session started on
  // the Secrets page is still there when publishing from the Registry page.
  return (
    <GooglePlaySessionProvider>
      <InvitationDialog />
      <PylonWidget />
      <Outlet />
    </GooglePlaySessionProvider>
  );
};

export const App = () => {
  console.log({ commit: import.meta.env.VITE_COMMIT });
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/signedStream" element={<SignedStreamPage />} />
        <Route element={<AuthProvider><Outlet /></AuthProvider>}>
          <Route path="/authn/login" element={<Login />} />
          <Route path="/authn/callback" element={<Callback />} />
          {/* OAuth page handles its own auth - token comes from URL params after SSO redirect */}
          <Route path="/authn/oauth" element={<OAuthAuthorize />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<ProtectedLayout />}>
              {/* Onboarding is simply the landing page; every page stays freely browsable. */}
              <Route path="/" element={<Navigate to="/onboarding" replace />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/authn/cli" element={<CLILogin />} />
              <Route path="/instances" element={<Instances />} />
              <Route path="/registry" element={<Registry />} />
              <Route path="/secrets" element={<Secrets />} />
              <Route path="/network" element={<Network />} />
              <Route path="/audit-logs" element={<AuditLogs />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/preview" element={<PreviewPage />} />
              <Route path="/stream/:instanceId?" element={<StreamPage />} />
              <Route path="/builds/:instanceId" element={<BuildsPage />} />
              <Route path="/bazel" element={<BazelBuildsPage />} />
              <Route path="/snapshots/:key?" element={<SnapshotsPage />} />
              <Route path="/sessions/:platform/:instanceId" element={<SessionsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Route>
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
};

// biome-ignore lint/style/noNonNullAssertion: It's how React works.
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
